import { Text } from "@mantine/core";
import { LoadingSpinner } from "@src/components/loading-spinner";
import clsx from "clsx";
import { getReferralProgress } from "../lib/get-referral-progress";

interface ReferralProgressSectionProps {
	validReferrals: number;
	isPending: boolean;
}

export function ReferralProgressSection({
	validReferrals,
	isPending,
}: ReferralProgressSectionProps) {
	const { monthsEarned, progressInCurrentGroup, remaining, isInPhase1 } =
		getReferralProgress(validReferrals);

	return (
		<div className="rounded-xl bg-amber-500/10 border border-amber-500/20 p-4 space-y-3">
			{/* Earned counter */}
			<div className="flex items-center justify-between">
				<Text
					size="xs"
					fw={700}
					className="uppercase tracking-wider text-text-muted"
				>
					Free Months Earned
				</Text>
				<div className="flex items-center gap-1.5">
					{isPending ? (
						<LoadingSpinner
							width={16}
							height={16}
							style={{ color: "#c9a900" }}
						/>
					) : (
						<span className="text-xl font-bold text-amber-500 tabular-nums">
							{monthsEarned}
						</span>
					)}
					<span aria-hidden="true" className="text-base leading-none">
						🏆
					</span>
				</div>
			</div>

			{/* Phase 1: two individual milestone slots (bonus tier) */}
			{isInPhase1 && (
				<div className="space-y-2.5">
					<div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
						✨ Early Bonus · You both get 1 free month
					</div>
					<div className="flex items-center gap-3">
						{[0, 1].map((slot) => {
							const earned = slot < validReferrals;
							return (
								<div key={slot} className="flex items-center gap-1.5">
									<div
										className={clsx(
											"w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300",
											earned
												? "bg-amber-400 border-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.4)]"
												: "bg-surface-default border-border-default",
										)}
									>
										{earned && (
											<span className="text-white text-[9px] font-bold">✓</span>
										)}
									</div>
									<span
										className={clsx(
											"text-sm select-none",
											earned ? "text-amber-400" : "text-text-muted",
										)}
									>
										→
									</span>
									<span className="text-base leading-none">
										{earned ? "🎁" : "🔒"}
									</span>
								</div>
							);
						})}
					</div>
					<Text size="xs" c="dimmed" className="leading-tight">
						{validReferrals === 0
							? "For your first 2 referrals, both you and your invitee each get 1 free month!"
							: "One more invite — you and your friend will each get 1 free month!"}
					</Text>
				</div>
			)}

			{/* Phase 2: 3-dot group progress track */}
			{!isInPhase1 && (
				<div className="space-y-2.5">
					<div className="space-y-1">
						<div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-subtle text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
							Your friends still get 1 free month each
						</div>
						<p className="text-[10px] text-text-muted">
							You earn 1 free month for every 3 referrals
						</p>
					</div>
					<div className="flex items-center gap-2.5">
						<div className="flex items-center gap-1.5">
							{[0, 1, 2].map((i) => {
								const filled = i < progressInCurrentGroup;
								return (
									<div
										key={i}
										className={clsx(
											"w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300",
											filled
												? "bg-amber-400 border-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.4)]"
												: "bg-surface-default border-border-default",
										)}
									>
										{filled && (
											<div className="w-2.5 h-2.5 rounded-full bg-white/80" />
										)}
									</div>
								);
							})}
						</div>
						<span className="text-text-muted select-none">→</span>
						<span
							className="text-base leading-none"
							aria-label="Your free month reward"
						>
							🎁
						</span>
						<Text size="xs" c="dimmed" className="tabular-nums ml-1">
							{progressInCurrentGroup}/3
						</Text>
					</div>
					<Text size="xs" c="dimmed" className="leading-tight">
						{remaining === 1
							? "1 more referral to earn your next free month"
							: `${remaining} more referrals to earn your next free month`}
					</Text>
				</div>
			)}
		</div>
	);
}
